/**
 * Waar de woning staat: adres, kaartpositie en of hij in de gids mag.
 *
 * Deze drie horen op één scherm omdat ze één vraag beantwoorden. Een adres zonder
 * coördinaten zet geen speld op de kaart en is voor de gids dus onvindbaar;
 * coördinaten zonder adres zeggen een gast niets. En meedoen aan de gids heeft alleen
 * betekenis als het eerste twee kloppen — vandaar dat de schakelaar hier staat en niet
 * bij de publicatie-instellingen.
 *
 * De API kende deze velden al sinds stage 1; alleen bood geen enkel scherm ze aan.
 * Dat is precies het gat waar regel 11 over gaat, en de reden dat dit bestand er is.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { MapView } from '../components/map-view'
import { useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import { fetchResidence, updateResidence } from '../lib/residence-client'
import type { Residence } from '../lib/residence-client'

interface Form {
  addressLine1: string
  addressLine2: string
  postalCode: string
  locality: string
  region: string
  country: string
  latitude: number | null
  longitude: number | null
  directoryListed: boolean
}

function formOf(residence: Residence): Form {
  return {
    addressLine1: residence.location.addressLine1,
    addressLine2: residence.location.addressLine2,
    postalCode: residence.location.postalCode,
    locality: residence.location.locality,
    region: residence.location.region,
    country: residence.location.country,
    latitude: residence.location.latitude,
    longitude: residence.location.longitude,
    directoryListed: residence.directoryListed,
  }
}

/** Zes decimalen is ongeveer een tiende meter; meer is schijnnauwkeurigheid. */
function round(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000
}

export function LocationPage(): ReactElement {
  const t = useTranslate()
  const [residence, setResidence] = useState<Residence | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lookingUp, setLookingUp] = useState(false)
  const [bounds, setBounds] = useState<[number, number, number, number] | null>(null)
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current) {
      return
    }
    loadedRef.current = true

    void (async () => {
      const list = await listResidences()
      if (!list.ok || list.data.length === 0) {
        setError(t('dashboard.location.error.load'))
        return
      }
      const first = list.data[0]
      if (first === undefined) {
        return
      }
      const detail = await fetchResidence(first.id)
      if (!detail.ok) {
        setError(t('dashboard.location.error.load'))
        return
      }
      setResidence(detail.data)
      setForm(formOf(detail.data))
    })()
  }, [t])

  /**
   * Het adres omzetten naar een punt op de kaart.
   *
   * Loopt via onze eigen API en niet rechtstreeks naar de geocoder: daar zit de cache
   * en de begrenzing die het gebruiksbeleid van Nominatim naleeft (§17.39). Een
   * verhuurder die tien keer op de knop drukt, stuurt hoogstens één verzoek naar
   * buiten.
   */
  const locate = useCallback(async (): Promise<void> => {
    if (form === null) {
      return
    }
    const parts = [form.addressLine1, form.postalCode, form.locality, form.country]
      .map((part) => part.trim())
      .filter((part) => part !== '')
    if (parts.length === 0) {
      return
    }

    setLookingUp(true)
    setError(null)
    try {
      const response = await fetch(
        `/api/public/directory/geocode?q=${encodeURIComponent(parts.join(', '))}`,
        { headers: { accept: 'application/json' } },
      )
      const payload: unknown = await response.json()
      const place =
        typeof payload === 'object' && payload !== null
          ? (payload as { place?: { latitude?: number; longitude?: number } }).place
          : null

      if (place?.latitude === undefined || place.longitude === undefined) {
        setError(t('dashboard.location.error.notFound'))
        return
      }
      setForm({ ...form, latitude: round(place.latitude), longitude: round(place.longitude) })
    } catch {
      setError(t('dashboard.location.error.notFound'))
    } finally {
      setLookingUp(false)
    }
  }, [form, t])

  async function save(): Promise<void> {
    if (residence === null || form === null) {
      return
    }
    setBusy(true)
    setSaved(false)
    setError(null)

    const result = await updateResidence(residence.id, {
      addressLine1: form.addressLine1.trim(),
      addressLine2: form.addressLine2.trim(),
      postalCode: form.postalCode.trim(),
      locality: form.locality.trim(),
      region: form.region.trim(),
      country: form.country.trim().toUpperCase(),
      latitude: form.latitude,
      longitude: form.longitude,
      directoryListed: form.directoryListed,
    })
    setBusy(false)

    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setResidence(result.data)
    setForm(formOf(result.data))
    setSaved(true)
  }

  if (form === null) {
    return (
      <section>
        <h1>{t('dashboard.location.title')}</h1>
        {error === null ? <p>{t('dashboard.location.loading')}</p> : <p role="alert">{error}</p>}
      </section>
    )
  }

  const placed = form.latitude !== null && form.longitude !== null

  return (
    <section className="location">
      <h1>{t('dashboard.location.title')}</h1>
      <p className="location__intro">{t('dashboard.location.intro')}</p>

      {error !== null ? <p role="alert">{error}</p> : null}
      {saved ? <p role="status">{t('dashboard.location.saved')}</p> : null}

      <div className="location__grid">
        <div>
          <h2>{t('dashboard.location.address')}</h2>

          <label>
            <span>{t('dashboard.location.field.addressLine1')}</span>
            <input
              value={form.addressLine1}
              onChange={(event) => {
                setForm({ ...form, addressLine1: event.target.value })
              }}
            />
          </label>
          <label>
            <span>{t('dashboard.location.field.addressLine2')}</span>
            <input
              value={form.addressLine2}
              onChange={(event) => {
                setForm({ ...form, addressLine2: event.target.value })
              }}
            />
          </label>
          <label>
            <span>{t('dashboard.location.field.postalCode')}</span>
            <input
              value={form.postalCode}
              onChange={(event) => {
                setForm({ ...form, postalCode: event.target.value })
              }}
            />
          </label>
          <label>
            <span>{t('dashboard.location.field.locality')}</span>
            <input
              value={form.locality}
              onChange={(event) => {
                setForm({ ...form, locality: event.target.value })
              }}
            />
          </label>
          <label>
            <span>{t('dashboard.location.field.region')}</span>
            <input
              value={form.region}
              onChange={(event) => {
                setForm({ ...form, region: event.target.value })
              }}
            />
          </label>
          <label>
            <span>{t('dashboard.location.field.country')}</span>
            <input
              value={form.country}
              maxLength={2}
              style={{ maxWidth: '6rem', textTransform: 'uppercase' }}
              onChange={(event) => {
                setForm({ ...form, country: event.target.value })
              }}
            />
          </label>

          <button
            type="button"
            className="button"
            disabled={lookingUp}
            onClick={() => {
              void locate()
            }}
          >
            {lookingUp
              ? t('dashboard.location.locating')
              : t('dashboard.location.locateFromAddress')}
          </button>
        </div>

        <div>
          <h2>{t('dashboard.location.onTheMap')}</h2>
          <p className="location__hint">{t('dashboard.location.mapHint')}</p>

          <MapView
            latitude={form.latitude}
            longitude={form.longitude}
            bounds={bounds}
            ariaLabel={t('dashboard.location.mapLabel')}
            onPick={(latitude, longitude) => {
              setBounds(null)
              setForm({ ...form, latitude: round(latitude), longitude: round(longitude) })
            }}
          />

          <p className="location__coords">
            {placed
              ? t('dashboard.location.placedAt', {
                  latitude: String(form.latitude),
                  longitude: String(form.longitude),
                })
              : t('dashboard.location.notPlaced')}
          </p>

          {placed ? (
            <button
              type="button"
              className="button"
              onClick={() => {
                setBounds(null)
                setForm({ ...form, latitude: null, longitude: null })
              }}
            >
              {t('dashboard.location.clear')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="location__directory">
        <h2>{t('dashboard.location.directory')}</h2>
        <label className="location__toggle">
          <input
            type="checkbox"
            checked={form.directoryListed}
            onChange={(event) => {
              setForm({ ...form, directoryListed: event.target.checked })
            }}
          />
          <span>{t('dashboard.location.directoryLabel')}</span>
        </label>
        <p className="location__hint">{t('dashboard.location.directoryHint')}</p>
        {form.directoryListed && !placed ? (
          // Meedoen zonder coördinaten betekent wél in de lijst en niet op de kaart.
          // Dat stilzwijgend laten gebeuren is precies het soort halve uitkomst dat
          // een verhuurder pas weken later ontdekt.
          <p className="location__warning">{t('dashboard.location.directoryNeedsPin')}</p>
        ) : null}
      </div>

      <button
        type="button"
        className="button button--primary"
        disabled={busy}
        onClick={() => {
          void save()
        }}
      >
        {busy ? t('dashboard.location.saving') : t('dashboard.location.save')}
      </button>
    </section>
  )
}
